import {useEffect, useLayoutEffect, useRef, useState, type RefObject} from 'react'
import {useMediaQuery} from '@/hooks/useMediaQuery'
import {flushSync} from 'react-dom'

/** Exit finishes before committing navigation. Rapid repeated actions cannot skip turns. */
export function useExitTransition() {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const ref = useRef<HTMLDivElement>(null)
  const pending = useRef(false)
  const animation = useRef<Animation | null>(null)
  useEffect(() => () => { animation.current?.cancel() }, [])
  async function advance(commit: () => void, blur = false) {
    if (pending.current) return
    pending.current = true
    const root = ref.current
    const target = blur ? root : root?.querySelector<HTMLElement>('[data-turn-body], form[data-current-question]') ?? root
    if (root) root.inert = true
    try {
      if (target && !reducedMotion) {
        const frames = blur ? [{opacity: 1, filter: 'blur(0px)'}, {opacity: 0, filter: 'blur(10px)'}] : [{opacity: 1, transform: 'translateY(0)'}, {opacity: 0, transform: 'translateY(28px)'}]
        animation.current = target.animate(frames, {duration: blur ? 320 : 220, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards'})
        await animation.current.finished
      }
      // Commit the successor before clearing the held exit frame. Otherwise
      // React's batched update lets the old form flash back for one paint.
      flushSync(commit)
    } catch { /* A removed turn cancels its pending animation. */ }
    finally {
      animation.current?.cancel()
      animation.current = null
      if (root) root.inert = false
      pending.current = false
    }
  }
  return {ref, advance}
}

/** Keep text empty until the new turn has arrived at the thread's reading edge. */
export function useTurnEntrance(ref: RefObject<HTMLElement | null>, key: string) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [arrived, setArrived] = useState<string | null>(null)
  useLayoutEffect(() => {
    const element = ref.current, thread = element?.closest('main')
    if (!element || !thread) return
    let frame = 0, canceled = false, finished = false
    const top = Math.max(0, Math.min(thread.scrollHeight - thread.clientHeight,
      thread.scrollTop + element.getBoundingClientRect().top - thread.getBoundingClientRect().top))
    thread.scrollTo({top, behavior: reducedMotion ? 'instant' : 'smooth'})
    const finish = () => {
      if (canceled || finished) return
      finished = true
      cancelAnimationFrame(frame)
      thread.removeEventListener('scrollend', finish)
      setArrived(key)
      element.focus({preventScroll:true})
    }
    const settled = () => {
      if (Math.abs(thread.scrollTop - top) < 1) finish()
      else frame = requestAnimationFrame(settled)
    }
    // scrollend also handles a user intentionally interrupting the movement.
    thread.addEventListener('scrollend', finish, {once:true})
    frame = requestAnimationFrame(settled)
    return () => { canceled = true; cancelAnimationFrame(frame); thread.removeEventListener('scrollend', finish) }
  }, [key, ref, reducedMotion])
  return arrived === key
}
